import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DictionaryAPIService } from 'src/app/extensions/services/api/dictionary-api.service';
import { RequestAPIService } from 'src/app/extensions/services/api/request-api.service';
import { Location } from '@angular/common';
import { DICTIONARY_REQUEST_TYPES, REQUEST_DETAIL_CATEGORY } from 'src/app/extensions/constants/commonConstants';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { DateInputFillMode, DateInputRounded, DateInputSize } from '@progress/kendo-angular-dateinputs';
import { NotificationService } from 'src/app/core/services/notification.service';
import { DictionaryRequestStatus } from 'src/app/extensions/constants/dictionaryConstants/dictionaryConstants';
import { ContractModificationRequestEvents } from 'src/app/extensions/services/api/events';

@Component({
  selector: 'app-contract-modification-detail',
  templateUrl: './contract-modification-detail.component.html',
  styleUrls: ['./contract-modification-detail.component.scss'],
  standalone: false,
})
export class ContractModificationDetailComponent implements OnInit {
  contractModificationRequestHeader: app.models.ContractModificationRequestHeader;

  contractModificationRequest: app.models.ContractModificationRequest = {} as app.models.ContractModificationRequest;
  props: app.models.DynamicPropsDictionary;

  reasonDatasource: app.models.DictionaryDataModel[];

  REIMBURSEMENT: app.models.DictionaryDataModel;
  INSTALLMENT_DEFERRAL: app.models.DictionaryDataModel;
  OTHER: app.models.DictionaryDataModel;
  FINANCING_OFFER: app.models.DictionaryRequestTypeModel;
  COMPLAINTS: app.models.DictionaryRequestTypeModel;
  CONTRACT_ASSIGNMENT: app.models.DictionaryRequestTypeModel;
  DIRECT_DEBIT: app.models.DictionaryDataModel;

  PARTIAL_REIMBURSEMENT: app.models.DictionaryDataModel;
  FULL_REIMBURSEMENT: app.models.DictionaryDataModel;
  statuses: app.models.DictionaryDataModel[];

  dataLoaded: boolean = false;

  REQUEST_DETAIL_CATEGORY = REQUEST_DETAIL_CATEGORY;
  DICTIONARY_REQUEST_TYPES = DICTIONARY_REQUEST_TYPES;
  selectedCategory: string = REQUEST_DETAIL_CATEGORY.INFO;

  isAdmin: boolean = false;
  contractModificationRequestChangeStatusModel: app.models.ContractModificationRequestChangeStatusModel =
    {} as app.models.ContractModificationRequestChangeStatusModel;

  public fillModeNone: DateInputFillMode = 'none';
  public roundedLarge: DateInputRounded = 'large';
  public sizeLarge: DateInputSize = 'large';

  public statusId: string = '';
  public dictionaryRequestStatus = DictionaryRequestStatus;

  constructor(
    private _requestAPIService: RequestAPIService,
    private _activatedRoute: ActivatedRoute,
    private _dictionaryAPIService: DictionaryAPIService,
    private _translateService: TranslateService,
    private _location: Location,
    private _globalService: GlobalService,
    private _notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.isAdmin = this._globalService.isAdminUser();
    this.contractModificationRequest.contractModificationRequestId = this._activatedRoute.snapshot.params['id'];
    if (!this.contractModificationRequest.contractModificationRequestId) {
      return;
    }
    if (this._activatedRoute.snapshot.queryParams['openTab']) {
      this.selectedCategory = this._activatedRoute.snapshot.queryParams['openTab'];
    }
    this.loadData();
  }

  async loadData() {
    let contractModificationResult = await this._requestAPIService
      .getContractModificationRequest(this.contractModificationRequest.contractModificationRequestId)
      .toPromise();
    let modificationTypeResult = await this._dictionaryAPIService
      .getDictionaryContractModificationRequestType()
      .toPromise();
    let reimbursementTypeResult = await this._dictionaryAPIService.getDictionaryReimbursementType().toPromise();
    this.contractModificationRequest = contractModificationResult.data;
    this.props = contractModificationResult.props;
    if (this.contractModificationRequest.contractId) {
      this._requestAPIService
        .getContractModificationRequestHeader(this.contractModificationRequest.contractId)
        .subscribe(response => {
          this.contractModificationRequestHeader = response;
        });
    }
    this.statusId = this.contractModificationRequest.dictionaryRequestStatus.id.toLowerCase();
    this._dictionaryAPIService.getDictionaryRequestStatuses().subscribe(statusResult => {
      if (statusResult.data) {
        this.statuses = statusResult.data.map(x => ({ ...x, id: x.id.toLowerCase() }));
      }
    });
    this.REIMBURSEMENT = modificationTypeResult.find(x => x.code == 'RQST_REIMBURSEMENT');
    this.INSTALLMENT_DEFERRAL = modificationTypeResult.find(x => x.code == 'RQST_INSTALLMENT_DEFERRAL');
    this.OTHER = modificationTypeResult.find(x => x.code == 'RQST_OTHER');
    this.FINANCING_OFFER = modificationTypeResult.find(x => x.code === 'RQST_FINANCING_OFFER');
    this.COMPLAINTS = modificationTypeResult.find(x => x.code === 'RQST_COMPLAINTS');
    this.CONTRACT_ASSIGNMENT = modificationTypeResult.find(x => x.code === 'RQST_CONTRACT_ASSIGNMENT');
    this.DIRECT_DEBIT = modificationTypeResult.find(x => x.code === 'RQST_DIRECT_DEBIT');

    this._dictionaryAPIService
      .getDictionaryContractModificationRequestReason(
        contractModificationResult.data.dictionaryContractModificationRequestTypeId,
        contractModificationResult.data.dictionaryReimbursementTypeId
      )
      .subscribe(x => {
        this.reasonDatasource = x;
        this.previewReasonOfRequest();
      });

    this.FULL_REIMBURSEMENT = reimbursementTypeResult.find(x => x.code == 'Full');
    this.PARTIAL_REIMBURSEMENT = reimbursementTypeResult.find(x => x.code == 'Partial');
    this.dataLoaded = true;
  }

  reasonOfRequest: string;
  previewReasonOfRequest() {
    if (!this.reasonDatasource) {
      return;
    }
    const reason = this.reasonDatasource.find(
      x =>
        x.id.toLowerCase() ==
        this.contractModificationRequest.dictionaryContractModificationRequestReasonId.toLowerCase()
    );
    let translation: string = '';

    if (reason?.code.toLowerCase().includes('other')) {
      translation = `${this._translateService.instant('contractModifications.wizard.preview.other')}: "${this.contractModificationRequest.otherReason}"`;
    } else {
      translation = reason?.value;
    }

    this.reasonOfRequest = translation;
  }

  redirectListHandler() {
    this._location.back();
  }

  categoryChangeHandler(selectedCategory: string) {
    this.selectedCategory = selectedCategory;
  }

  changeContractModificationRequestStatus(statusId: string) {
    const initialStatus = this.contractModificationRequest.dictionaryRequestStatus;
    let statusCode = this.statuses.find(x => x.id == statusId).code;
    this.contractModificationRequestChangeStatusModel.requestId =
      this.contractModificationRequest.contractModificationRequestId;
    this.contractModificationRequestChangeStatusModel.dictionaryRequestStatusCode = statusCode;
    this._requestAPIService
      .changeContractModificationRequestStatus(this.contractModificationRequestChangeStatusModel)
      .subscribe(result => {
        if (result.isSuccess) {
          this._notificationService.showSuccess(
            this._translateService.instant('request.statusChange.notifications.requestStatusChanged'),
            this._translateService.instant('common.success')
          );
          let currentStatus = this.statuses.find(
            x =>
              x.id ===
              result.response.events.find(
                y => y.eventType === ContractModificationRequestEvents.ContractModificationRequestStatusChanged
              ).eventBody?.dictionaryRequestStatusId
          );

          this.contractModificationRequest.dictionaryRequestStatus = currentStatus;
        } else {
          this._notificationService.showError(
            this._translateService.instant('request.statusChange.notifications.requestStatusChangeFailed'),
            this._translateService.instant('common.error')
          );
          this.contractModificationRequest.dictionaryRequestStatus = initialStatus;
        }
      });
  }
}
